/**
 * `markdownRoundtrip` — convert between the canonical markdown body and the
 * HTML form Tiptap's ProseMirror parser ingests.
 *
 * Why this exists (2026-05-18):
 *
 * Tiptap is a ProseMirror wrapper. ProseMirror's document model is canonical;
 * markdown is not a primitive. The standard pattern for markdown-first apps is
 * to pivot through HTML on both directions:
 *
 *   storage (markdown)
 *     → markdown→HTML via `marked`
 *     → ProseMirror `parseHTML` (schema-coerce — UNKNOWN INPUT BECOMES VALID DOC)
 *     → editor doc
 *     → HTML serialise via `editor.getHTML()`
 *     → HTML→markdown via `turndown`
 *     → storage (markdown)
 *
 * This trades a perfectly-stable markdown round-trip (which MDXEditor sort of
 * had, modulo its own normalisations) for a fundamentally different failure
 * mode: ProseMirror's HTML parser is the same code browsers use for
 * `contentEditable`, and when it hits an unknown construct it coerces to the
 * nearest schema-valid node (usually a text node). The result is never empty.
 *
 * That's the whole point of moving off MDXEditor — silent-blank failures on
 * unknown inputs (Joplin `<style>` blocks, Plaud Python lists, nested HTML+md
 * combinations) become "the formatting looks weird" failures instead.
 *
 * Known fidelity costs:
 *
 *   - Bullet markers may flip `*` ↔ `-` (we configure turndown to use `-`)
 *   - Emphasis delimiters may flip `*` ↔ `_` (we configure turndown to use `_`)
 *   - Reference-style links serialise as inline links
 *   - Some inline HTML survives as raw HTML in the output
 *
 * These are the same class of losses MDXEditor already imposed via its
 * normalisation (it had `emDelimiter`, `bulletListMarker`, etc.). The shape
 * of the loss is the same; the specifics differ.
 *
 * The backend treats markdown as structural-not-formatting (chunk hashing is
 * content-based, embedding is text-based), so this fidelity loss is invisible
 * to the rest of the system.
 */

import { marked } from 'marked';
import TurndownService from 'turndown';
import { tables } from 'turndown-plugin-gfm';

// ─── markdown → HTML ────────────────────────────────────────────────────────
// Configure marked with GFM (strikethrough, tables, task lists) and breaks
// disabled (CommonMark behaviour — single newline is a line continuation,
// double newline is a paragraph break). Tiptap's parser handles the resulting
// HTML directly.

marked.setOptions({
  gfm: true,
  breaks: false,
  // Tiptap's contentEditable handles soft breaks; we don't need marked to.
});

// Wikilink extension — `[[Note Title]]` → `<a class="wikilink" data-title="…">Title</a>` so
// Tiptap parses it into the `WikilinkNode` (a clickable inline chip). Mirrors the preview's
// extension (renderMarkdown.ts). Anchored on `[[` so it never collides with `[text](url)`. The
// chip serializes back to `[[Title]]` via the turndown rule below — the body stays byte-stable.
marked.use({
  extensions: [
    {
      name: 'wikilink',
      level: 'inline',
      start(src: string) {
        return src.indexOf('[[');
      },
      tokenizer(src: string) {
        const m = /^\[\[([^\]\n]+)\]\]/.exec(src);
        if (!m) return undefined;
        return { type: 'wikilink', raw: m[0], text: m[1].trim() };
      },
      renderer(token: { text?: string } & Record<string, unknown>) {
        const raw = String((token as { text?: string }).text ?? '');
        const safe = raw
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
        return `<a class="wikilink" data-title="${safe}" href="#wikilink">${safe}</a>`;
      },
    },
  ],
});

/**
 * Convert a markdown string into the HTML form Tiptap's parser ingests.
 * Returns a string of HTML suitable for `editor.commands.setContent(html)`.
 *
 * Edge cases:
 *   - Empty string → empty string (Tiptap accepts this and shows a blank doc)
 *   - Plain text → wrapped in `<p>...</p>` by marked
 *   - HTML already in the markdown passes through marked unchanged (per CommonMark)
 */
export function markdownToEditorHtml(markdown: string): string {
  if (markdown.length === 0) return '';
  // `marked.parse` is sync when no async extensions are loaded; we cast to
  // string to satisfy the union return type.
  return marked.parse(markdown) as string;
}

// ─── HTML → markdown ────────────────────────────────────────────────────────
// Lazy-init Turndown (cheap but not free; reuse the instance). Configure to
// match our markdown style guide: `-` bullets, `_` italic, `**` bold, fenced
// code blocks, ATX headings.

let _turndown: TurndownService | null = null;

function turndown(): TurndownService {
  if (_turndown !== null) return _turndown;
  const t = new TurndownService({
    headingStyle: 'atx',
    codeBlockStyle: 'fenced',
    bulletListMarker: '-',
    emDelimiter: '_',
    strongDelimiter: '**',
    linkStyle: 'inlined',
    br: '  ',
  });

  // GFM tables: base Turndown has NO table rule — without this, a table created in the
  // rich editor serializes to nothing and is silently LOST on save (data-loss bug fixed
  // 2026-06-10). The `tables` plugin from turndown-plugin-gfm adds the GFM pipe-table
  // rules. We use ONLY `tables` (not the whole `gfm` bundle) so it doesn't collide with
  // the hand-rolled strikethrough / taskItem rules below.
  t.use(tables);

  // Override the cell rule (Turndown checks later-added rules first, so this wins over the
  // plugin's): Tiptap wraps each cell's content in a block `<p>`, which the plugin's cell
  // rule turns into `\n\nText\n\n` — embedded newlines that BREAK the one-line-per-row GFM
  // table syntax (and would fail to re-parse on load). Flatten cell content to a single
  // line and escape literal pipes so the round-trip is valid markdown.
  t.addRule('tableCellFlatten', {
    filter: ['th', 'td'] as never,
    replacement: (content, node) => {
      const parent = (node as HTMLElement).parentNode;
      const index =
        parent !== null ? Array.prototype.indexOf.call(parent.childNodes, node) : 0;
      const prefix = index === 0 ? '| ' : ' ';
      const flat = content
        .replace(/\r?\n+/g, ' ') // collapse the <p> block newlines to a space
        .replace(/\|/g, '\\|') // escape literal pipes so they don't split the cell
        .trim();
      return `${prefix}${flat} |`;
    },
  });

  // GFM extensions: strikethrough + task lists need explicit rules (hand-rolled below).

  // ~~strikethrough~~
  t.addRule('strikethrough', {
    filter: ['del', 's', 'strike'] as never,
    replacement: (content) => `~~${content}~~`,
  });

  // Highlight (`<mark>`) + colored text (`<span style="color: …">`). Markdown
  // has no native syntax for either, so we KEEP them as raw inline HTML on the
  // way out (the same canonical representation they had on the way in). Without
  // these rules turndown's default strips the wrapper and keeps only the inner
  // text, silently dropping the highlight / colour.
  t.addRule('highlightMark', {
    filter: 'mark',
    replacement: (content) => `<mark>${content}</mark>`,
  });
  t.addRule('coloredSpan', {
    filter: (node) =>
      node.nodeName === 'SPAN' &&
      (node as HTMLElement).style !== undefined &&
      (node as HTMLElement).style.color !== '',
    replacement: (content, node) => {
      const color = (node as HTMLElement).style.color;
      return `<span style="color: ${color}">${content}</span>`;
    },
  });
  // Text alignment (center/right/justify) on paragraphs + headings. Markdown has no
  // alignment syntax, so — like colour + highlight — we KEEP aligned blocks as raw HTML
  // on the way out; `marked` passes the HTML back through and the TextAlign extension
  // re-parses the `text-align` style on the way in. Default (left) blocks are untouched
  // and serialise as normal markdown.
  t.addRule('textAlignBlock', {
    filter: (node) => {
      const el = node as HTMLElement;
      const align = el.style !== undefined ? el.style.textAlign : '';
      return (
        ['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(node.nodeName) &&
        align !== '' &&
        align !== 'left'
      );
    },
    replacement: (content, node) => {
      const tag = node.nodeName.toLowerCase();
      const align = (node as HTMLElement).style.textAlign;
      return `\n\n<${tag} style="text-align: ${align}">${content.trim()}</${tag}>\n\n`;
    },
  });

  // Resized / aligned images: markdown `![](url)` has no width OR alignment syntax, so an image
  // carrying a `width` attr (drag-resize) and/or a `text-align` style (the toolbar align buttons,
  // 2026-06-23) is KEPT as raw `<img … width=… style="text-align:…">` HTML — `marked` passes it
  // back through and AttachmentImage + TextAlign re-parse the width + alignment on load. A plain
  // (unsized, left-aligned) image has neither, so turndown's default emits clean `![alt](src)`.
  t.addRule('imageWithSize', {
    filter: (node) => {
      if (node.nodeName !== 'IMG') return false;
      const el = node as HTMLElement;
      const styleW = el.style !== undefined ? el.style.width : '';
      const align = el.style !== undefined ? el.style.textAlign : '';
      return (
        el.getAttribute('width') !== null ||
        styleW !== '' ||
        (align !== '' && align !== 'left')
      );
    },
    replacement: (_content, node) => {
      const el = node as HTMLElement;
      const src = el.getAttribute('src') || '';
      const alt = el.getAttribute('alt') || '';
      const styleW = el.style !== undefined ? el.style.width : '';
      const width = el.getAttribute('width') || (styleW ? String(parseInt(styleW, 10)) : '');
      const align = el.style !== undefined ? el.style.textAlign : '';
      const keepAlign = align !== '' && align !== 'left';
      // Nothing special to preserve → clean markdown.
      if (!width && !keepAlign) return `![${alt}](${src})`;
      const altAttr = alt ? ` alt="${alt}"` : '';
      const widthAttr = width ? ` width="${width}"` : '';
      const styleAttr = keepAlign ? ` style="text-align: ${align}"` : '';
      return `<img src="${src}"${altAttr}${widthAttr}${styleAttr}>`;
    },
  });

  // Wikilink chips → `[[Title]]`. The rich editor's WikilinkNode renders as
  // `<a class="wikilink" data-title="Title">`; serialize it back to the canonical wikilink
  // markdown (NOT a `[text](url)` link — this rule is checked before turndown's built-in link
  // rule, so the `data-note-id` / href="#wikilink" never leak into the body). Byte-stable.
  t.addRule('wikilink', {
    filter: (node) =>
      node.nodeName === 'A' &&
      (node as HTMLElement).classList !== undefined &&
      (node as HTMLElement).classList.contains('wikilink'),
    replacement: (_content, node) => {
      const el = node as HTMLElement;
      const title = (el.getAttribute('data-title') || el.textContent || '').trim();
      return `[[${title}]]`;
    },
  });

  // - [ ] / - [x] task list items. Tiptap emits these as `<li data-type="taskItem"
  //   data-checked="true|false"><label>…</label><div>…</div></li>`. We flatten
  //   into the GFM shape.
  t.addRule('taskItem', {
    filter: (node) =>
      node.nodeName === 'LI' &&
      (node as HTMLElement).getAttribute('data-type') === 'taskItem',
    replacement: (content, node) => {
      const checked =
        (node as HTMLElement).getAttribute('data-checked') === 'true';
      // Strip the leading newline turndown adds for list items so the
      // checkbox sits flush to the marker.
      const inner = content.replace(/^\n+/, '').trim();
      return `- [${checked ? 'x' : ' '}] ${inner}\n`;
    },
  });

  _turndown = t;
  return t;
}

/**
 * Serialise the editor's HTML back to canonical markdown for storage.
 *
 * Args:
 *   html: The string `editor.getHTML()` returns. Already valid HTML.
 *
 * Returns:
 *   Markdown text suitable for the canonical body field.
 *
 * Edge cases:
 *   - Empty string → empty string
 *   - HTML that wraps just `<p></p>` → empty string (treats "doc with one
 *     empty paragraph" as canonically empty)
 */
export function editorHtmlToMarkdown(html: string): string {
  if (html.length === 0) return '';
  // Empty-paragraph fast path: Tiptap's "empty" doc is `<p></p>`, which
  // turndown would render as a literal empty string anyway. Make it
  // explicit so callers don't have to handle the whitespace edge case.
  if (html === '<p></p>') return '';
  // Tiptap's resizable tables emit a `<colgroup>` (per-column widths) as the table's first
  // child. That makes `<tbody>` NOT the table's first child, which breaks the GFM table
  // rule's heading-row detection — so it falls back to KEEPING the table as raw HTML
  // instead of serializing a pipe table (the data-loss symptom, 2026-06-10). Strip the
  // colgroup (it carries only presentational widths) so the table serializes to markdown.
  const cleaned = html.replace(/<colgroup[\s\S]*?<\/colgroup>/gi, '');
  return turndown().turndown(cleaned);
}
